import axios from 'axios';
import config from '../config';

// Create axios instance
const httpClient = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - Add auth token
httpClient.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem(config.storageKeys.accessToken);
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle token refresh
httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is not 401 or request already retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If refresh is already in progress, queue the request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return httpClient(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem(config.storageKeys.refreshToken);

    if (!refreshToken) {
      // No refresh token, dispatch auth expired event
      window.dispatchEvent(new CustomEvent('AUTH_EXPIRED'));
      return Promise.reject(error);
    }

    try {
      // Attempt to refresh tokens
      const response = await axios.post(`${config.apiUrl}/platform/refresh`, {
        refreshToken
      });

      if (response.data?.success) {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;

        // Store new tokens
        localStorage.setItem(config.storageKeys.accessToken, accessToken);
        localStorage.setItem(config.storageKeys.refreshToken, newRefreshToken);

        // Update authorization header
        httpClient.defaults.headers.Authorization = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        // Process queued requests
        processQueue(null, accessToken);

        return httpClient(originalRequest);
      }
    } catch (refreshError) {
      // Refresh failed, dispatch auth expired event
      processQueue(refreshError, null);
      window.dispatchEvent(new CustomEvent('AUTH_EXPIRED'));
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }

    return Promise.reject(error);
  }
);

export default httpClient;
